import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/api";
import Layout from "../components/Layout";
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faList,
    faEnvelopeOpenText,
    faCheckCircle,
    faArrowLeft,
    faPhone,
    faEnvelope,
    faBuilding,
    faTrash,
    faCheck,
    faBriefcase,
    faCalendarAlt,
    faTag,
    faTimesCircle,
    faLock,
    faSyncAlt,
    faCopy,
    faPaperPlane
} from '@fortawesome/free-solid-svg-icons';

const STAGES = [
    "Qualification",
    "Approached",
    "Follow-up",
    "Meeting Scheduled",
    "Proposal Preparation",
    "Review/Discussion",
    "Completed"
];

const LinkedInIcon = () => (
    <svg className="lead-linkedin-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.2V10.9H6.46M7.83 6.45a1.64 1.64 0 1 0 0 3.28 1.64 1.64 0 0 0 0-3.28z" />
    </svg>
);

const formatLinkedInUrl = (url) => {
    if (!url || !url.trim()) return '';
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    if (trimmed.startsWith('www.') || trimmed.startsWith('linkedin.com')) {
        return `https://${trimmed}`;
    }
    return `https://www.linkedin.com/in/${trimmed}`;
};

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return 'SV';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'SV';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const OpportunityDetail = ({ id: propId, onBack, onClose }) => {
    const params = useParams();
    const navigate = useNavigate();
    const id = propId || params.id;
    const { currentUser, empId, teamMemberIds, isSuper, isManager } = useAuth();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState("");
    const [submittingComment, setSubmittingComment] = useState(false);
    const [actionMessage, setActionMessage] = useState("");
    const [copiedField, setCopiedField] = useState("");

    const canEditOpportunity = useMemo(() => {
        if (!data) return false;
        if (isSuper) return true;
        if (isManager) {
            const teamIds = [empId, ...(teamMemberIds || []).map(Number)];
            return teamIds.includes(Number(data.Owner));
        }
        // Employee: only opportunity owner has edit access (lead generator gets view-only)
        return Number(data.Owner) === Number(empId);
    }, [data, isSuper, isManager, empId, teamMemberIds]);

    const isLeadGenOnly = useMemo(() => {
        if (!data) return false;
        return !canEditOpportunity && Number(data.LeadGenerator) === Number(empId);
    }, [data, canEditOpportunity, empId]);

    // Standard Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        type: '', // 'STAGE' | 'STATUS'
        targetValue: '',
        title: '',
        message: ''
    });

    // Special Completion Outcome Modal State (Win vs Lose)
    const [completionModal, setCompletionModal] = useState({
        open: false,
        title: '',
        message: ''
    });

    const handleBack = () => {
        if (typeof onBack === "function") onBack();
        else if (typeof onClose === "function") onClose();
        else navigate("/opportunities");
    };

    const showNotification = (msg) => {
        setActionMessage(msg);
        setTimeout(() => setActionMessage(""), 3500);
    };

    const handleCopy = (text, fieldName) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(fieldName);
        setTimeout(() => setCopiedField(""), 2000);
        showNotification(`Copied ${fieldName} to clipboard`);
    };

    const fetchOpportunity = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/opportunities/${id}`);
            setData(res.data);
        } catch (err) {
            console.error("Failed to load opportunity:", err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    const fetchComments = useCallback(async () => {
        try {
            const res = await api.get(`/opportunities/${id}/comments`);
            setComments(res.data || []);
        } catch (err) {
            console.error("Failed to load comments:", err);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchOpportunity();
            fetchComments();
        }
    }, [id, fetchOpportunity, fetchComments]);

    // Stage change request handler
    const requestStageChange = (newStage) => {
        if (!canEditOpportunity) {
            showNotification("View-only access: Only the opportunity owner can transition stages.");
            return;
        }
        if (!data) return;

        if (newStage === 'Completed') {
            setCompletionModal({
                open: true,
                title: 'Complete Opportunity Deal',
                message: `You are moving "${data.OpprName}" to the Completed stage. Please confirm whether this deal was a Win or a Lose:`
            });
            return;
        }

        if (data.Stage === newStage) return;

        setConfirmModal({
            open: true,
            type: 'STAGE',
            targetValue: newStage,
            title: 'Confirm Stage Transition',
            message: `Are you sure you want to transition this opportunity from "${data.Stage || 'Qualification'}" to "${newStage}"?`
        });
    };

    // Handle outcome chosen in Completion Modal (Win or Lose)
    const handleCompleteWithOutcome = async (outcome) => {
        setCompletionModal({ open: false, title: '', message: '' });
        try {
            await api.patch(`/opportunities/${id}/status`, {
                Status: outcome,
                Stage: 'Completed'
            });
            setData(prev => ({
                ...prev,
                Status: outcome,
                Stage: 'Completed',
                UpdatedAt: new Date().toISOString()
            }));
            showNotification(`Opportunity marked as Completed (${outcome === 'Win' ? 'Won' : 'Lost'})`);
        } catch (err) {
            console.error("Completion error:", err);
            alert("Failed to complete opportunity");
        }
    };

    // Deal outcome request handler
    const requestStatusChange = (newStatus) => {
        if (!canEditOpportunity) {
            showNotification("View-only access: Only the opportunity owner can change deal status.");
            return;
        }
        if (!data || data.Status === newStatus) return;

        if (newStatus === 'Win' || newStatus === 'Lose') {
            setConfirmModal({
                open: true,
                type: 'STATUS',
                targetValue: newStatus,
                title: `Confirm Deal Outcome: ${newStatus}`,
                message: `Marking this deal as "${newStatus}" will automatically advance its pipeline stage to "Completed". Do you want to proceed?`
            });
        } else {
            setConfirmModal({
                open: true,
                type: 'STATUS',
                targetValue: newStatus,
                title: 'Reopen Opportunity',
                message: `Are you sure you want to set the deal status to "${newStatus}"?`
            });
        }
    };

    // Execute Confirmed Change
    const executeConfirmedAction = async () => {
        if (!confirmModal.open) return;
        const { type, targetValue } = confirmModal;
        setConfirmModal({ open: false, type: '', targetValue: '', title: '', message: '' });

        try {
            if (type === 'STAGE') {
                await api.patch(`/opportunities/${id}/stage`, { Stage: targetValue });
                setData(prev => ({ ...prev, Stage: targetValue, UpdatedAt: new Date().toISOString() }));
                showNotification(`Opportunity stage advanced to "${targetValue}"`);
            } else if (type === 'STATUS') {
                const stageToSet = (targetValue === 'Win' || targetValue === 'Lose') ? 'Completed' : data.Stage;
                await api.patch(`/opportunities/${id}/status`, {
                    Status: targetValue,
                    Stage: stageToSet
                });
                setData(prev => ({
                    ...prev,
                    Status: targetValue,
                    Stage: stageToSet,
                    UpdatedAt: new Date().toISOString()
                }));
                showNotification(
                    targetValue === 'Open'
                        ? 'Deal status set to Open'
                        : `Deal marked as "${targetValue}" and stage updated to "Completed"`
                );
            }
        } catch (err) {
            console.error("Action confirmation error:", err);
            alert("Failed to update opportunity");
        }
    };

    const handlePostComment = async (e) => {
        if (e) e.preventDefault();
        if (!newComment.trim() || submittingComment) return;

        try {
            setSubmittingComment(true);
            const author = currentUser?.name || data?.OwnerName || "Sales Representative";
            const email = currentUser?.email || data?.OwnerEmail || "";

            const res = await api.post(`/opportunities/${id}/comments`, {
                CommentText: newComment.trim(),
                AuthorName: author,
                AuthorEmail: email
            });

            setComments(prev => [res.data, ...prev]);
            setNewComment("");
            showNotification("Comment posted to activity timeline");
        } catch (err) {
            console.error("Failed to post comment:", err);
            alert("Failed to post comment");
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await api.delete(`/opportunities/comments/${commentId}`);
            setComments(prev => prev.filter(c => c.CommentID !== commentId));
            showNotification("Comment removed");
        } catch (err) {
            console.error("Delete comment error:", err);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        try {
            return new Date(dateStr).toLocaleString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        } catch {
            return String(dateStr);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="dome-dashboard-wrap">
                    <div className="dome-dash-card p-24 text-center text-muted">
                        Loading opportunity details...
                    </div>
                </div>
            </Layout>
        );
    }

    if (!data) {
        return (
            <Layout>
                <div className="dome-dashboard-wrap">
                    <div className="dome-dash-header-row">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={handleBack}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            <span>Back to Opportunities</span>
                        </button>
                    </div>

                    <div className="dome-dash-card p-24 text-center">
                        <h3 className="m-0 text-dark">Opportunity record not found</h3>
                        <p className="text-muted text-13 mt-6">The opportunity record may have been removed or you do not have permission to view it.</p>
                        <button
                            type="button"
                            className="btn-dome-new mt-12"
                            onClick={handleBack}
                        >
                            Return to Pipeline
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const currentStageIndex = STAGES.indexOf(data.Stage);

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* NOTIFICATION TOAST */}
                {actionMessage && (
                    <div className="slds-toast-notification">
                        <FontAwesomeIcon icon={faCheck} className="mr-8px" />
                        {actionMessage}
                    </div>
                )}

                {/* ================= 1. TOP HEADER & BREADCRUMB ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={handleBack}
                            title="Return to Opportunities Directory"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            <span>Back to Opportunities</span>
                        </button>
                    </div>

                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            onClick={() => { fetchOpportunity(); fetchComments(); }}
                            title="Refresh opportunity details"
                        >
                            <FontAwesomeIcon icon={faSyncAlt} />
                            <span>Refresh</span>
                        </button>
                    </div>
                </div>

                {/* ================= VIEW ONLY LEAD GENERATOR BANNER ================= */}
                {isLeadGenOnly && (
                    <div className="leadgen-viewonly-banner">
                        <FontAwesomeIcon icon={faLock} className="leadgen-viewonly-icon" />
                        <span>
                            <strong>View-Only Access (Lead Generator):</strong> You generated this lead account and have read-only access to this opportunity. Deal stage, status, and details can only be edited by the Opportunity Owner (<strong>{data.OwnerName || 'Assigned Owner'}</strong>).
                        </span>
                    </div>
                )}

                {/* ================= 2. HERO HIGHLIGHT CARD ================= */}
                <div className="dome-opp-hero-card">
                    <div className="dome-opp-hero-left">
                        <div className="dome-opp-hero-icon">
                            <FontAwesomeIcon icon={faBriefcase} />
                        </div>

                        <div className="dome-opp-hero-meta">
                            <div className="dome-opp-hero-title-row">
                                <h1 className="dome-opp-hero-title">{data.OpprName}</h1>
                            </div>

                            <div className="dome-opp-hero-subtags">
                                <span
                                    className="dome-opp-hero-subtag linkable"
                                    onClick={() => navigate('/leads')}
                                    title="View client account in Leads"
                                >
                                    <span>{data.AccountName || "Unassigned Account"}</span>
                                </span>

                                <span className="dome-opp-hero-subtag">

                                    <span>Created: {formatDate(data.CreatedAt)}</span>
                                    <span className="lead-id-badge">Opp ID: {data.OprID}</span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="dome-opp-hero-right">
                        {!canEditOpportunity && (
                            <span className="badge-view-only-pill">
                                <FontAwesomeIcon icon={faLock} className="icon-lock-sm" />
                                View Only
                            </span>
                        )}

                        {/* Status Change Buttons with Confirmation */}
                        <div className="dome-status-btn-group">
                            <button
                                type="button"
                                className={`dome-status-btn ${data.Status === 'Win' ? 'active-win' : ''}`}
                                onClick={() => canEditOpportunity && requestStatusChange('Win')}
                                disabled={!canEditOpportunity}
                                title={canEditOpportunity ? "Mark deal as Win" : "View-only: Only opportunity owner can change status"}
                            >
                                <FontAwesomeIcon icon={faCheckCircle} />
                                <span>Win</span>
                            </button>

                            <button
                                type="button"
                                className={`dome-status-btn ${data.Status === 'Open' ? 'active-open' : ''}`}
                                onClick={() => canEditOpportunity && requestStatusChange('Open')}
                                disabled={!canEditOpportunity}
                                title={canEditOpportunity ? "Set deal as Open" : "View-only: Only opportunity owner can change status"}
                            >
                                <span>Open</span>
                            </button>

                            <button
                                type="button"
                                className={`dome-status-btn ${data.Status === 'Lose' ? 'active-lose' : ''}`}
                                onClick={() => canEditOpportunity && requestStatusChange('Lose')}
                                disabled={!canEditOpportunity}
                                title={canEditOpportunity ? "Mark deal as Lose" : "View-only: Only opportunity owner can change status"}
                            >
                                <FontAwesomeIcon icon={faTimesCircle} />
                                <span>Lose</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ================= 3. STAGE PROGRESSION PATH ================= */}
                <div className="dome-stage-path-card">
                    <div className="dome-stage-path-nav">
                        {STAGES.map((stg, index) => {
                            const isCurrent = stg === data.Stage;
                            const isPast = currentStageIndex > -1 && index < currentStageIndex;
                            let statusClass = "is-incomplete";
                            if (isCurrent) statusClass = "is-current";
                            else if (isPast) statusClass = "is-complete";

                            return (
                                <div
                                    key={stg}
                                    className={`dome-stage-path-step ${statusClass} ${!canEditOpportunity ? 'disabled' : ''}`}
                                    onClick={() => {
                                        if (canEditOpportunity) {
                                            requestStageChange(stg);
                                        } else {
                                            showNotification("View-only: Only opportunity owner can transition stages.");
                                        }
                                    }}
                                    title={canEditOpportunity ? `Click to transition stage to ${stg}` : "View-only: Only opportunity owner can transition stages"}
                                >
                                    {isPast ? (
                                        <FontAwesomeIcon icon={faCheck} className="step-check" />
                                    ) : (
                                        <span className="step-check">{index + 1}. </span>
                                    )}
                                    <span className="stage-name">{stg}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* ================= 4. 2-COLUMN DETAIL GRID ================= */}
                <div className="dome-opp-grid-2col">

                    {/* LEFT COLUMN: SPECIFICATIONS & CLIENT PROFILE */}
                    <div className="dome-opp-col">

                        {/* Card 1: Key Opportunity Information */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">
                                    <FontAwesomeIcon icon={faTag} className="mr-6px text-primary" />
                                    Key Opportunity Information
                                </h3>
                            </div>

                            <table className="dome-detail-table">
                                <tbody>
                                    <tr>
                                        <th>Opportunity Name</th>
                                        <td><strong>{data.OpprName}</strong></td>
                                        <th>Amount (USD)</th>
                                        <td>
                                            <span className="dome-opp-amount text-success font-w-600 text-14">
                                                {Number(data.Amount || 0).toLocaleString("en-US")}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Pipeline Stage</th>
                                        <td>
                                            <span style={{
                                                color: "#1677ff",
                                                fontWeight: "bold"
                                            }}>
                                                {data.Stage || "Qualification"}
                                            </span>
                                        </td>
                                        <th>Deal Status</th>
                                        <td>
                                            <span>
                                                {data.Status || "Open"}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Opportunity Owner</th>
                                        <td>
                                            <strong>{data.OwnerName || "System Administrator"}</strong>
                                        </td>
                                        <th>Created Date</th>
                                        <td>{formatDate(data.CreatedAt)}</td>
                                    </tr>
                                    {data.UpdatedAt && (
                                        <tr>
                                            <th>Last Updated</th>
                                            <td colSpan={3}>{formatDate(data.UpdatedAt)}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Card 2: Client Account & Contact Profile */}
                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">
                                    <FontAwesomeIcon icon={faBuilding} className="mr-6px text-primary" />
                                    Client Account &amp; Contact Profile
                                </h3>
                            </div>

                            <table className="dome-detail-table">
                                <tbody>
                                    <tr>
                                        <th>Account / Client</th>
                                        <td>
                                            <strong>{data.AccountName || "Unassigned"}</strong>
                                        </td>
                                        <th>Account Type</th>
                                        <td>
                                            {data.AccountType === 'Individual' ? (
                                                <span>
                                                    Individual Practitioner
                                                </span>
                                            ) : (
                                                <span>
                                                    Organization
                                                </span>
                                            )}
                                        </td>
                                    </tr>

                                    {String(data.AccountStatus || '').toLowerCase() === 'lead' && (
                                        <tr>
                                            <th>Lead Generator</th>
                                            <td colSpan={3}>
                                                {data.LeadGeneratorName ? (
                                                    <div className="d-inline-flex align-center gap-6">
                                                        <span className="text-dark font-w-500">
                                                            {data.LeadGeneratorName}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Unassigned</span>
                                                )}
                                            </td>
                                        </tr>
                                    )}

                                    <tr>
                                        <th>Primary Phone</th>
                                        <td>
                                            {data.CntPhone1 ? (
                                                <div className="slds-action-val-group">
                                                    <a href={`tel:${data.CntPhone1}`} className="slds-link-action">
                                                        <FontAwesomeIcon icon={faPhone} className="mr-4px" />
                                                        {data.CntPhone1}
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="slds-copy-btn"
                                                        onClick={() => handleCopy(data.CntPhone1, 'Phone')}
                                                        title="Copy Phone"
                                                    >
                                                        <FontAwesomeIcon icon={faCopy} />

                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-muted">None registered</span>
                                            )}
                                        </td>

                                        <th>Primary Email</th>
                                        <td>
                                            {data.CntMail1 ? (
                                                <div className="slds-action-val-group">
                                                    <a href={`mailto:${data.CntMail1}`} className="slds-link-action">
                                                        <FontAwesomeIcon icon={faEnvelope} className="mr-4px" />
                                                        {data.CntMail1}
                                                    </a>
                                                    <button
                                                        type="button"
                                                        className="slds-copy-btn"
                                                        onClick={() => handleCopy(data.CntMail1, 'Email')}
                                                        title="Copy Email"
                                                    >
                                                        <FontAwesomeIcon icon={faCopy} />
                                                        <span>{copiedField === 'Email' ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            ) : (
                                                <span className="text-muted">None registered</span>
                                            )}
                                        </td>
                                    </tr>

                                    <tr>
                                        <th>LinkedIn Profile</th>
                                        <td colSpan={3}>
                                            {data.Linkedin ? (
                                                <a
                                                    href={formatLinkedInUrl(data.Linkedin)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="lead-linkedin-link"
                                                    title={data.Linkedin}
                                                >
                                                    <span>View Profile</span>
                                                </a>
                                            ) : (
                                                <span className="text-muted">None registered</span>
                                            )}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                    </div>

                    {/* RIGHT COLUMN: ACTIVITY TIMELINE & DISCUSSION */}
                    <div className="dome-opp-col">

                        <div className="dome-dash-card">
                            <div className="dome-dash-card-header">
                                <h3 className="dome-dash-card-title">
                                    <FontAwesomeIcon icon={faEnvelopeOpenText} className="mr-6px text-primary" />
                                    Discussion &amp; Activity Feed
                                </h3>
                            </div>

                            <div className="p-16">
                                {/* Comment Composer */}
                                <div className="comment-composer">
                                    <textarea
                                        className="comment-textarea"
                                        placeholder="Write remarks..."
                                        rows="1"
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                    />
                                    <div className="composer-actions">
                                        <button
                                            type="button"
                                            className="btn-dome-submit"
                                            onClick={handlePostComment}
                                            disabled={submittingComment || !newComment.trim()}
                                            title={submittingComment ? "Sending..." : "Send remark"}
                                        >
                                            <FontAwesomeIcon icon={submittingComment ? faSyncAlt : faPaperPlane} spin={submittingComment} />
                                        </button>
                                    </div>
                                </div>

                                {/* Activity Timeline */}
                                <div className="activity-timeline">
                                    {comments.length === 0 ? (
                                        <div className="empty-comments-state">
                                            <FontAwesomeIcon icon={faList} className="empty-state-list-icon" />
                                            <p>No activity notes recorded yet. Post the first update above.</p>
                                        </div>
                                    ) : (
                                        comments.map(c => (
                                            <div key={c.CommentID} className="timeline-item">
                                                <div className="timeline-avatar">
                                                    {getInitials(c.AuthorName)}
                                                </div>

                                                <div className="timeline-bubble">
                                                    <div className="bubble-header">
                                                        <div className="author-info">
                                                            <span className="author-name">{c.AuthorName}</span>
                                                            <span className="comment-date">{formatDate(c.CreatedAt)}</span>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            className="comment-delete-btn"
                                                            title="Delete note"
                                                            onClick={() => handleDeleteComment(c.CommentID)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>

                                                    <div className="bubble-text">
                                                        {c.CommentText}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>

                </div>

                {/* ================= STANDARD CONFIRMATION MODAL ================= */}
                {confirmModal.open && (
                    <div className="modal-overlay">
                        <div className="modal modal-confirm-md">
                            <div className="modal-header">
                                <h3>{confirmModal.title}</h3>
                            </div>
                            <div className="modal-body">
                                <p className="confirm-modal-text">
                                    {confirmModal.message}
                                </p>
                                <div className="modal-footer mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setConfirmModal({ open: false, type: '', targetValue: '', title: '', message: '' })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new"
                                        onClick={executeConfirmedAction}
                                    >
                                        Confirm Change
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* ================= COMPLETION OUTCOME MODAL (WIN vs LOSE) ================= */}
                {completionModal.open && (
                    <div className="modal-overlay" onClick={() => setCompletionModal({ open: false, title: '', message: '' })}>
                        <div className="modal outcome-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="outcome-header">
                                <h3 className="outcome-title">
                                    {completionModal.title}
                                </h3>
                            </div>
                            <div className="outcome-body">
                                <p className="outcome-desc">
                                    {completionModal.message}
                                </p>

                                <div className="outcome-grid">
                                    <button
                                        type="button"
                                        onClick={() => handleCompleteWithOutcome('Win')}
                                        className="outcome-btn-win"
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} className="outcome-icon" />
                                        <span>Won Deal (Win)</span>
                                        <span className="outcome-subtext">Stage: Completed &bull; Status: Win</span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleCompleteWithOutcome('Lose')}
                                        className="outcome-btn-lose"
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} className="outcome-icon" />
                                        <span>Lost Deal (Lose)</span>
                                        <span className="outcome-subtext">Stage: Completed &bull; Status: Lose</span>
                                    </button>
                                </div>
                            </div>

                            <div className="outcome-footer">
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={() => setCompletionModal({ open: false, title: '', message: '' })}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
};

export default OpportunityDetail;